import React from 'react';

import {
  NavigationDirection,
  SNAP_BACK_TRANSITION,
  SWIPE_OUT_TRANSITION,
  SWIPE_THRESHOLD,
  SWIPE_VERTICAL_THRESHOLD_MULTIPLIER
} from '../constants';
import {
  isInVideoControlArea,
  getSwipeTarget,
  isNavButton,
  getElementWidth
} from '../utils';

export type UseSwipeArgs = {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  ref: React.RefObject<HTMLElement | null>;
  disabled: boolean;
};

const useSwipe = ({
  onSwipeLeft,
  onSwipeRight,
  ref,
  disabled
}: UseSwipeArgs): void => {
  const startX = React.useRef(0);
  const startY = React.useRef(0);
  const isDragging = React.useRef(false);
  const hasMoved = React.useRef(false);
  const isSwipingOut = React.useRef(false);
  const onSwipeLeftCallback = React.useRef(onSwipeLeft);
  const onSwipeRightCallback = React.useRef(onSwipeRight);

  const animateSwipeOut = React.useCallback(
    (target: HTMLElement, direction: NavigationDirection) => {
      if (isSwipingOut.current) {
        return;
      }

      isSwipingOut.current = true;
      const elementWidth = getElementWidth(target);
      const translateX =
        direction === NavigationDirection.PREV ? -elementWidth : elementWidth;

      target.style.transition = SWIPE_OUT_TRANSITION;
      target.style.transform = `translateX(${translateX}px)`;

      const cleanup = () => {
        target.style.transition = '';
        target.style.transform = '';
        isSwipingOut.current = false;
        target.removeEventListener('transitionend', cleanup);
      };

      target.addEventListener('transitionend', cleanup, { once: true });
    },
    []
  );

  const resetSwipeState = React.useCallback(() => {
    isDragging.current = false;
    hasMoved.current = false;
    isSwipingOut.current = false;

    if (!ref.current) {
      return;
    }

    const target = getSwipeTarget(ref.current);
    if (target) {
      target.style.transition = '';
      target.style.transform = '';
    }
  }, [ref]);

  React.useEffect(() => {
    const element = ref.current;

    if (!element || disabled) {
      return;
    }

    const handleTouchStart = (event: TouchEvent) => {
      if (
        isNavButton(event.target) ||
        isInVideoControlArea(event.target, event.touches[0].clientY)
      ) {
        return;
      }

      if (isSwipingOut.current) {
        return;
      }

      startX.current = event.touches[0].clientX;
      startY.current = event.touches[0].clientY;
      isDragging.current = true;
      hasMoved.current = false;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!isDragging.current || isSwipingOut.current) {
        return;
      }

      const deltaX = event.touches[0].clientX - startX.current;
      const deltaY = event.touches[0].clientY - startY.current;

      if (
        Math.abs(deltaY) >
        Math.abs(deltaX) * SWIPE_VERTICAL_THRESHOLD_MULTIPLIER
      ) {
        return;
      }

      const target = getSwipeTarget(element);
      if (target) {
        if (!hasMoved.current) {
          target.style.transition = 'none';
          hasMoved.current = true;
        }
        target.style.transform = `translateX(${deltaX}px)`;
      }
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (!isDragging.current) {
        return;
      }

      isDragging.current = false;

      if (!hasMoved.current) {
        return;
      }

      const deltaX = event.changedTouches[0].clientX - startX.current;
      const deltaY = event.changedTouches[0].clientY - startY.current;

      startX.current = 0;
      startY.current = 0;

      const target = getSwipeTarget(element);
      const thresholdMet =
        Math.abs(deltaX) >= SWIPE_THRESHOLD &&
        Math.abs(deltaX) > Math.abs(deltaY);

      if (target) {
        if (thresholdMet) {
          const isNext = deltaX > 0;
          const direction = isNext
            ? NavigationDirection.NEXT
            : NavigationDirection.PREV;
          animateSwipeOut(target, direction);

          if (isNext) {
            onSwipeRightCallback.current();
          } else {
            onSwipeLeftCallback.current();
          }
        } else {
          target.style.transition = SNAP_BACK_TRANSITION;
          target.style.transform = '';

          const cleanup = () => {
            target.style.transition = '';
            target.removeEventListener('transitionend', cleanup);
          };
          target.addEventListener('transitionend', cleanup, { once: true });
        }
      }
    };

    window.addEventListener('beforeunload', resetSwipeState);
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('beforeunload', resetSwipeState);
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      resetSwipeState();
    };
  }, [ref, disabled]);
};

export default useSwipe;
